/**
 * Plugin view redirects (T-20260915-1, T-20260915-2, T-20260915-3, T-20260915-4)
 *
 * The per-plugin calendar, board and dashboard URLs now land on the unified
 * cross-plugin pages:
 *   /plugins/:pluginId/calendar  → /calendar?plugin=:pluginId&view=calendar
 *   /plugins/:pluginId/kanban    → /calendar?plugin=:pluginId&view=board
 *   /plugins/:pluginId/dashboard → /dashboards?plugin=:pluginId
 *   /plugins/:pluginId/settings  → /settings/plugins?plugin=:pluginId
 *   /plugins/:pluginId/library   → /library?plugins=:pluginId
 *
 * Replace navigation; every incoming query param and the hash are preserved
 * (`?item=` above all — the PU-6.8 deep link `PluginCalendarPage` reads). The route's plugin
 * (and view, where there is one) wins over a stray incoming param. Callers
 * linking to the old URLs are deliberately left alone.
 */

import { Navigate, useLocation, useParams } from 'react-router-dom';
import { LIBRARY_PLUGIN_PARAM } from '@/lib/hooks/useLibraryPluginFilter';

/**
 * `routeParams` are set first and override the same keys arriving in the
 * query; every other incoming param is appended in order.
 */
function buildTarget(
  pathname: string,
  routeParams: [string, string][],
  incomingSearch: string,
): string {
  const incoming = new URLSearchParams(incomingSearch);
  const next = new URLSearchParams();
  const owned = new Set<string>();
  for (const [key, value] of routeParams) {
    next.set(key, value);
    owned.add(key);
  }
  for (const [key, value] of incoming) {
    if (owned.has(key)) continue;
    next.append(key, value);
  }
  return `${pathname}?${next.toString()}`;
}

function PluginViewRedirect({
  pathname,
  pluginParam = 'plugin',
  extraParams = [],
}: {
  pathname: string;
  /** The destination page's plugin query key (`plugin` unless stated). */
  pluginParam?: string;
  extraParams?: [string, string][];
}) {
  const { pluginId = '' } = useParams<{ pluginId: string }>();
  const { search, hash } = useLocation();
  const target = buildTarget(pathname, [[pluginParam, pluginId], ...extraParams], search);
  return <Navigate to={`${target}${hash}`} replace />;
}

/**
 * `/plugins/:pluginId/calendar` → `/calendar?plugin=:pluginId&view=calendar`
 *
 * The route decides the view, overriding any incoming `?view=`. Without an
 * explicit `view=calendar`, a plugin that also has a board (and no saved
 * preference) would resolve to Board — which never reads `?item=`, losing the
 * deep link every "Back to Calendar" caller relies on.
 */
export function PluginCalendarRedirect() {
  return <PluginViewRedirect pathname="/calendar" extraParams={[['view', 'calendar']]} />;
}

/** `/plugins/:pluginId/kanban` → `/calendar?plugin=:pluginId&view=board` */
export function PluginKanbanRedirect() {
  return <PluginViewRedirect pathname="/calendar" extraParams={[['view', 'board']]} />;
}

/**
 * `/plugins/:pluginId/dashboard` → `/dashboards?plugin=:pluginId` (T-20260915-2)
 *
 * `/dashboards` is the board switcher (Epic 8 story 8.3). Since story 8.5 it
 * resolves `?plugin=` itself, once its board list has loaded: the plugin's
 * seeded board (`sourcePluginId`) is opened at `/dashboards/<boardId>`, else
 * the URL is replaced with `/dashboards`. This redirect stays a pure route —
 * only the page has the board list.
 */
export function PluginDashboardRedirect() {
  return <PluginViewRedirect pathname="/dashboards" />;
}

/** `/plugins/:pluginId/settings` → `/settings/plugins?plugin=:pluginId` (T-20260915-3) */
export function PluginSettingsRedirect() {
  return <PluginViewRedirect pathname="/settings/plugins" />;
}

/**
 * `/plugins/:pluginId/library` → `/library?plugins=:pluginId` (T-20260915-4)
 *
 * The per-plugin Library page was dropped; the main Library filters by plugin
 * through its own comma-list key (`LIBRARY_PLUGIN_PARAM`, `plugins`), not the
 * `plugin` key the other destinations read.
 *
 * `?doc=<id>` is the exception: the deleted page turned it into a navigation to
 * the document, and `DocumentLibraryPage` never reads `doc`. So a non-empty
 * `doc` replace-navigates straight to the `/library/:documentId` detail route
 * instead of the filtered list — which keeps the legacy
 * `/campaigns/:id` → `/plugins/marketing/library?doc=:id` chain landing on the
 * document. Without `doc`, every other param and the hash are carried over.
 */
export function PluginLibraryRedirect() {
  const { search } = useLocation();
  const doc = new URLSearchParams(search).get('doc');
  if (doc) {
    return <Navigate to={`/library/${encodeURIComponent(doc)}`} replace />;
  }
  return <PluginViewRedirect pathname="/library" pluginParam={LIBRARY_PLUGIN_PARAM} />;
}
